import { FilterSection } from "@/components/search-filter/FilterSection";
import { OptionItem } from "@/types/OptionItem";
import { Indent } from "@stenajs-webui/core";
import { Spinner, Tag, stenaSailingRoute } from "@stenajs-webui/elements";
import { FilterCheckbox } from "@stenajs-webui/filter";
import { CollapsibleWithCheckbox } from "@stenajs-webui/panels";
import { ReactNode, useContext, useMemo, useState } from "react";
import {
  PriceListRoute,
  useFetchPriceListRoutes,
} from "../../hooks/UseFetchPriceListRoutes";
import {
  buildRouteTree,
  routeCodesInPair,
  routeCodesInSeaArea,
  toggleGroup,
  toggleRoute,
} from "../../lib/routeTree";
import {
  PriceListFilterContext,
  PriceListFilterContextType,
  sectionIdConstants,
} from "./PriceListFilterContext";

const routeOption = (route: PriceListRoute): OptionItem => ({
  label: `${route.code} - ${route.name}`,
  value: route.code,
});

const GroupCollapsible = ({
  label,
  groupCodes,
  selectedCodes,
  onToggle,
  children,
}: {
  label: string;
  groupCodes: string[];
  selectedCodes: Set<string>;
  onToggle: () => void;
  children: ReactNode;
}) => {
  const [collapsed, setCollapsed] = useState(true);
  const selectedCount = groupCodes.filter((code) =>
    selectedCodes.has(code)
  ).length;

  return (
    <CollapsibleWithCheckbox
      label={label}
      value={selectedCount === groupCodes.length}
      indeterminate={selectedCount > 0 && selectedCount !== groupCodes.length}
      onChange={onToggle}
      collapsed={collapsed}
      onClick={() => setCollapsed(!collapsed)}
      contentRight={
        selectedCount > 0 && <Tag label={String(selectedCount)} size="small" />
      }
    >
      {children}
    </CollapsibleWithCheckbox>
  );
};

export const RoutesFilterSection = () => {
  const { filterState, dispatch, toggleSectionId, openSectionIds } = useContext(
    PriceListFilterContext
  ) as PriceListFilterContextType;
  const { routes, loading } = useFetchPriceListRoutes();
  const sectionId = sectionIdConstants.routes;

  const tree = useMemo(() => buildRouteTree(routes), [routes]);

  const routesByCode = useMemo(
    () => new Map(routes.map((route) => [route.code, route])),
    [routes]
  );

  const selectedCodes = useMemo(
    () => new Set(filterState.selectedRoutes.map((route) => route.value)),
    [filterState.selectedRoutes]
  );

  const setCodes = (codes: string[]) =>
    dispatch({
      type: "SET_ROUTES",
      payload: codes.flatMap((code) => {
        const route = routesByCode.get(code);
        return route ? [routeOption(route)] : [];
      }),
    });

  const currentCodes = () =>
    filterState.selectedRoutes.map(({ value }) => value);

  return (
    <FilterSection
      leftIcon={stenaSailingRoute}
      label={"Routes *"}
      isOpen={!!openSectionIds.includes(sectionId)}
      setIsOpen={() => toggleSectionId(sectionId)}
    >
      {loading ? (
        <Spinner />
      ) : (
        tree.map((seaArea) => (
          <GroupCollapsible
            key={seaArea.seaAreaName}
            label={seaArea.seaAreaName}
            groupCodes={routeCodesInSeaArea(seaArea)}
            selectedCodes={selectedCodes}
            onToggle={() =>
              setCodes(
                toggleGroup(currentCodes(), routeCodesInSeaArea(seaArea))
              )
            }
          >
            {seaArea.routePairs.map((routePair) => (
              <Indent key={routePair.routePairCode} num={2}>
                <GroupCollapsible
                  label={routePair.routePairCode}
                  groupCodes={routeCodesInPair(routePair)}
                  selectedCodes={selectedCodes}
                  onToggle={() =>
                    setCodes(
                      toggleGroup(currentCodes(), routeCodesInPair(routePair))
                    )
                  }
                >
                  <Indent num={2}>
                    {routePair.routes.map((route) => (
                      <FilterCheckbox
                        key={route.code}
                        label={route.code}
                        value={selectedCodes.has(route.code)}
                        onValueChange={() =>
                          setCodes(toggleRoute(currentCodes(), route.code))
                        }
                      />
                    ))}
                  </Indent>
                </GroupCollapsible>
              </Indent>
            ))}
          </GroupCollapsible>
        ))
      )}
    </FilterSection>
  );
};
